import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plug,
  RefreshCw,
  Upload,
  Download,
  Shield,
  AlertCircle,
  Mail,
  ExternalLink,
  CheckCircle2,
} from 'lucide-react';

/** Renders newline-separated steps as a numbered list. */
function StepList({ text }: { text: string }) {
  const lines = text.split('\n').filter(Boolean);
  return (
    <ol className="list-none space-y-2 mt-3">
      {lines.map((line, i) => {
        const content = line.replace(/^\d+\.\s*/, '');
        return (
          <li key={i} className="flex gap-3 text-sm text-slate-700">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold flex items-center justify-center">
              {i + 1}
            </span>
            <span className="pt-0.5 leading-relaxed">{content}</span>
          </li>
        );
      })}
    </ol>
  );
}

function Section({
  id,
  icon: Icon,
  title,
  children,
}: {
  id: string;
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
          <Icon className="w-4 h-4 text-violet-600" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      </div>
      <div className="pl-[2.625rem]">{children}</div>
    </section>
  );
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-3 flex gap-2 rounded-lg border border-violet-100 bg-violet-50/60 px-3 py-2.5 text-sm text-slate-700">
      <AlertCircle className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="mt-2 space-y-1.5">
      {items.map((item) => (
        <li key={item} className="flex gap-2 text-sm text-slate-700">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

const AshbyIntegrationHelp: React.FC = () => {
  const { t } = useTranslation('helpAshby');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toc = [
    { id: 'overview', label: t('toc.overview') },
    { id: 'requirements', label: t('toc.requirements') },
    { id: 'setup-ashby', label: t('toc.setupAshby') },
    { id: 'setup-cevee', label: t('toc.setupCevee') },
    { id: 'sync-jobs', label: t('toc.syncJobs') },
    { id: 'push', label: t('toc.push') },
    { id: 'pull', label: t('toc.pull') },
    { id: 'data', label: t('toc.data') },
    { id: 'troubleshooting', label: t('toc.troubleshooting') },
    { id: 'support', label: t('toc.support') },
  ];

  return (
    <div className="min-h-[calc(100vh-200px)] bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="border-b border-slate-200/80 bg-white/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
          <div className="flex items-center gap-2 text-xs font-medium text-violet-600 mb-3">
            <Plug className="w-3.5 h-3.5" />
            <span>{t('meta.audience')}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {t('meta.title')}
          </h1>
          <p className="mt-3 text-base text-slate-600 leading-relaxed max-w-2xl">
            {t('meta.subtitle')}
          </p>
          <p className="mt-4 text-xs text-slate-400">
            {t('meta.lastUpdated')}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Table of contents */}
        <nav className="mb-10 rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
            {t('toc.title')}
          </p>
          <ol className="grid sm:grid-cols-2 gap-1.5 text-sm">
            {toc.map(({ id, label }, i) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className="text-slate-600 hover:text-violet-600 hover:underline transition-colors"
                >
                  {i + 1}. {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-10 sm:space-y-12">
          <Section id="overview" icon={Plug} title={t('overview.title')}>
            <p className="text-sm text-slate-700 leading-relaxed">{t('overview.intro')}</p>
            <BulletList
              items={[
                t('overview.item1'),
                t('overview.item2'),
                t('overview.item3'),
                t('overview.item4'),
              ]}
            />
            <Tip>{t('overview.note')}</Tip>
          </Section>

          <Section id="requirements" icon={Shield} title={t('requirements.title')}>
            <BulletList
              items={[
                t('requirements.item1'),
                t('requirements.item2'),
                t('requirements.item3'),
              ]}
            />
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                {t('requirements.scopesTitle')}
              </p>
              <code className="text-sm font-mono text-slate-800">{t('requirements.scopes')}</code>
              <p className="mt-2 text-xs text-slate-500">{t('requirements.scopesNote')}</p>
            </div>
          </Section>

          <Section id="setup-ashby" icon={Download} title={t('setupAshby.title')}>
            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-1">
                  {t('setupAshby.marketplaceTitle')}
                </h3>
                <StepList text={t('setupAshby.marketplaceSteps')} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-1">
                  {t('setupAshby.manualTitle')}
                </h3>
                <StepList text={t('setupAshby.manualSteps')} />
              </div>
            </div>
            <Tip>{t('setupAshby.tip')}</Tip>
          </Section>

          <Section id="setup-cevee" icon={Plug} title={t('setupCevee.title')}>
            <StepList text={t('setupCevee.steps')} />
            <p className="mt-3 text-sm text-slate-600">{t('setupCevee.success')}</p>
          </Section>

          <Section id="sync-jobs" icon={RefreshCw} title={t('syncJobs.title')}>
            <p className="text-sm text-slate-700 leading-relaxed">{t('syncJobs.body')}</p>
            <Tip>{t('syncJobs.tip')}</Tip>
          </Section>

          <Section id="push" icon={Upload} title={t('push.title')}>
            <p className="text-sm text-slate-700 leading-relaxed">{t('push.body')}</p>
            <p className="mt-4 text-sm font-semibold text-slate-800">{t('push.flowTitle')}</p>
            <BulletList
              items={[t('push.flow1'), t('push.flow2'), t('push.flow3')]}
            />
            <p className="mt-4 text-sm font-semibold text-slate-800">{t('push.consentTitle')}</p>
            <p className="text-sm text-slate-700 leading-relaxed">{t('push.consent')}</p>
            <p className="mt-4 text-sm font-semibold text-slate-800">{t('push.limitsTitle')}</p>
            <p className="text-sm text-slate-700">{t('push.limits')}</p>
            <Tip>{t('push.ashbyNote')}</Tip>
          </Section>

          <Section id="pull" icon={Download} title={t('pull.title')}>
            <p className="text-sm text-slate-700 leading-relaxed">{t('pull.body')}</p>
            <StepList text={t('pull.steps')} />
            <Tip>{t('pull.note')}</Tip>
          </Section>

          <Section id="data" icon={Shield} title={t('data.title')}>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">{t('data.recruiterNote')}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  {t('data.pushTitle')}
                </p>
                <p className="text-sm text-slate-700">{t('data.pushItems')}</p>
                <p className="mt-2 text-xs text-slate-500">{t('data.pushOptional')}</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  {t('data.pullTitle')}
                </p>
                <p className="text-sm text-slate-700">{t('data.pullItems')}</p>
              </div>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              <li>{t('data.notTransferred')}</li>
              <li>{t('data.storage')}</li>
              <li>{t('data.thirdCountry')}</li>
            </ul>
            <p className="mt-3 text-sm text-slate-600">
              {t('data.legalLink')}{' '}
              <Link to="/privacy" className="text-violet-600 hover:underline">
                {t('data.privacyLink')}
              </Link>
              {' · '}
              <Link to="/terms" className="text-violet-600 hover:underline">
                {t('data.termsLink')}
              </Link>
            </p>
          </Section>

          <Section id="troubleshooting" icon={AlertCircle} title={t('troubleshooting.title')}>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex gap-2">
                <span className="text-slate-400">·</span>
                {t('troubleshooting.connectionFailed')}
              </li>
              <li className="flex gap-2">
                <span className="text-slate-400">·</span>
                {t('troubleshooting.noJobs')}
              </li>
              <li className="flex gap-2">
                <span className="text-slate-400">·</span>
                {t('troubleshooting.pushBlocked')}
              </li>
              <li className="flex gap-2">
                <span className="text-slate-400">·</span>
                {t('troubleshooting.cvMissing')}
              </li>
              <li className="flex gap-2">
                <span className="text-slate-400">·</span>
                {t('troubleshooting.slowPull')}
              </li>
              <li className="flex gap-2">
                <span className="text-slate-400">·</span>
                {t('troubleshooting.duplicate')}
              </li>
            </ul>
          </Section>

          <Section id="support" icon={Mail} title={t('support.title')}>
            <p className="text-sm text-slate-700">{t('support.body')}</p>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <a
                href={`mailto:${t('support.email')}`}
                className="inline-flex items-center gap-1.5 text-violet-600 hover:underline font-medium"
              >
                <Mail className="w-4 h-4" />
                {t('support.email')}
              </a>
              <a
                href={t('support.website')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-violet-600 hover:underline font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                cevee.eu
              </a>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default AshbyIntegrationHelp;
